import { pushIfNew, pushIfNewAndDefined, removeFirstFromArray } from "../collection.array.tools";
import { anyReallyAny } from "../object.tools";

export type Comparator<T> = (o1: T, o2: T) => boolean;

/**
 * creates map[key] if it does not exists
 * @returns map[key] or created array
 */
export function createArrayMapEntryIfUndefined<K, T>(map: Map<K, T[]>, key: K): T[] {
	let arr = map.get(key);
	if (!arr) { arr = []; map.set(key, arr); }
	return arr;
}

/**
 * map[key].push(value); and creates map[key] if it does not exists
 */
export function addToArrayMap<K, T>(map: Map<K, T[]>, key: K, value: T): void {
	createArrayMapEntryIfUndefined(map, key).push(value);
}

/**
 * map[key].push(value); if new and creates map[key] if it does not exists
 * @deprecated this function will be removed because it is not performant, if you want unique values, use a Set 
 * FIXME implement a SetMap
 */
export function addToArrayMapIfNew<K, T>(map: Map<K, T[]>, key: K, value: T, comparator?: (o1: T, o2: T) => boolean): boolean {
	return pushIfNew(createArrayMapEntryIfUndefined(map, key), value, comparator);
}

/**
 * map[key].push(value); if new and defined and creates map[key] if it does not exists
 * @deprecated this function will be removed because it is not performant, if you want unique values, use a Set 
 * FIXME implement a SetMap
 */
export function addToArrayMapIfNewAndDefined<K, T>(map: Map<K, T[]>, key: K, value: T, comparator?: (o1: T, o2: T) => boolean): boolean {
	return pushIfNewAndDefined(createArrayMapEntryIfUndefined(map, key), value, comparator);
}

/** 
 * Separates a list of objects according to their value on the field named "key"
 * returns an array indexed with all unique values for the field "key", each array containing matching objects
 * @deprecated use {@link buildArrayMap} or {@link buildArrayMapWithFunc}
 */
export function getObjectsByStringFieldMap<K extends keyof T, T>(objectList: T[], key: K, computeKeyFromObject?: (value: anyReallyAny) => K): Map<T[K], T[]> {
	const result: Map<T[K], T[]> = new Map<T[K], T[]>();
	for (const o of objectList) {
		addToArrayMap(result, computeKeyFromObject ? computeKeyFromObject(o[key]) : o[key], o);
	}
	return result;
}

/**
 * Equivalent to Map<K, T[]> with specialized function
 * 
 * ! when setting acceptNullKey to true you MUST define K as nullable => ArrayMap<Key|undefined,T>
 * 
 * TODO uniformize with objectMaps
 */
export class ArrayMap<K, T> extends Map<K, T[]> {
	private _comparator: Comparator<T> | undefined;

	constructor(
		objectList?: T[],
		keyGenerator?: ((obj: T) => K | undefined),
		p?: {
			acceptNullKey?: boolean,
			comparator?: Comparator<T>
		}
	) {
		super();
		this._comparator = p?.comparator;
		if (objectList && keyGenerator) {
			this.addDynamicKey(objectList, keyGenerator, p?.acceptNullKey);
		}
	}

	/** add list by generated key */
	public addDynamicKey(objectList: T[], keyGenerator: ((obj: T) => K | undefined), acceptNullKey = false): void {
		for (const o of objectList) {
			const k = keyGenerator(o);
			if (k != null || acceptNullKey) {
				this.add(k!, o);
			}
		}
	}

	/**
	 * map[key].push every values and creates map[key] if it does not exists
	 */
	public add(key: K, ...values: T[]): void {
		for (const value of values) {
			addToArrayMap(this, key, value);
		}
	}

	/**
	 * map[key].push(value); if new and creates map[key] if it does not exists
	 * @returns true if any of the value was added
	 */
	public addIfNew(key: K, ...values: T[]): boolean {
		let added = false;
		for (const value of values) {
			added ||= addToArrayMapIfNew(this, key, value, this._comparator);
		}
		return added;
	}

	/**
	 * map[key].push(value); if new and defined and creates map[key] if it does not exists
	 * @returns true if any of the value was added
	 */
	public addIfNewAndDefined(key: K, ...values: T[]): boolean {
		let added = false;
		for (const value of values) {
			added ||= addToArrayMapIfNewAndDefined(this, key, value, this._comparator);
		}
		return added;
	}

	/** 
	 * map[key].push every values; and creates map[key] if it does not exists 
	 */
	public addAll(key: K, values: T[]): void {
		this.add(key, ...values);
	}

	/**
	 * equivalent to `removeFirstFromArray(map[key], values)
	 */
	public removeFirst(key: K, ...values: T[]): void {
		this.removeFirstOfAll(key, values);
	}

	/**
	 * equivalent to `removeFirstFromArray(map[key], values, equal)`
	 */
	public removeFirstOfAll(key: K, values: T[], equal?: Comparator<T>): void {
		const arr = this.get(key);
		if (arr) {
			for (const value of values) {
				removeFirstFromArray(arr, value, equal);
			}
		}
	}
}

/**
 * Used to map an array by key/property of an object
 */
export class ArrayMapByKey<T, KeyOfT extends keyof T> extends ArrayMap<T[KeyOfT], T> {
	constructor(objectList?: T[], key?: KeyOfT, p?: { acceptNullKey: boolean }) {
		super();
		if (objectList && key) {
			this.mapObjectsByKey(objectList, key, p?.acceptNullKey);
		}
	}

	/** 
	 * Map an object list by key
	 */
	public mapObjectsByKey(objectList: T[], key: KeyOfT, acceptNullKey = false): void {
		for (const o of objectList) {
			const k = o[key];
			if (k != null || acceptNullKey) {
				this.add(k, o);
			}
		}
	}
}
